/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../../index";
import type { AppAction } from "../shared/types";

export const GetAppActionsParams = z.object({
  implementation_id: z
    .string()
    .describe(
      "The app implementation ID with version (e.g., 'GoogleSheetsV2CLIAPI@2.8.0') from get_app_name",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Loading app actions...' or 'Getting capabilities...'",
    ),
});

export type GetAppActionsParamsType = z.infer<typeof GetAppActionsParams>;

export const getAppActionsExecute = async (rawArgs: any): Promise<any> => {
  let params: GetAppActionsParamsType;
  try {
    params = GetAppActionsParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[GET_APP_ACTIONS] Error parsing arguments:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for get_app_actions. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { implementation_id } = params;

  try {
    // Validate implementation_id format
    if (!/^[A-Za-z0-9]+@\d+\.\d+\.\d+$/.test(implementation_id)) {
      return {
        $error: {
          message: `Invalid implementation_id format: ${implementation_id}. Expected format: AppName@version (e.g., GmailCLIAPI@1.5.0)`,
        },
      };
    }

    // Call our backend caching route
    const apiUrl = `/api/canvasui/composer/getAppActions?implementation_id=${encodeURIComponent(implementation_id)}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: `API returned ${response.status}: ${response.statusText}`,
        };
      }

      console.error(
        `[GET_APP_ACTIONS] API error: ${response.status}`,
        errorData,
      );

      return {
        $error: {
          message: `Failed to fetch app actions: ${errorData.error || response.statusText}`,
        },
      };
    }

    const data = await response.json();

    if (!data.success || !data.actions) {
      return {
        $error: {
          message: `Invalid response format from getAppActions API`,
        },
      };
    }

    const actions: AppAction[] = data.actions;

    if (actions.length === 0) {
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(
              {
                implementation_id,
                actions: [],
                message:
                  "No field mapping actions found for this app. The app may not support automation or may be configured differently.",
                total_raw_actions: data.total_actions || 0,
              },
              null,
              2,
            ),
          },
        ],
      };
    }

    // Group actions by type for better organization
    const actionsByType = actions.reduce(
      (acc, action) => {
        const type = action.type;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push({
          key: action.key,
          name: action.name,
          description: action.description,
          noun: action.noun,
          is_important: action.is_important,
        });
        return acc;
      },
      {} as Record<string, any[]>,
    );

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(
            {
              implementation_id,
              app_name: implementation_id.split("@")[0],
              actions_by_type: actionsByType,
              total_actions: actions.length,
              cache_info: "Actions cached for 60 minutes",
              summary: {
                write_actions: actionsByType.write?.length || 0,
                read_actions: actionsByType.read?.length || 0,
                search_actions:
                  (actionsByType.search?.length || 0) +
                  (actionsByType.search_or_write?.length || 0),
              },
            },
            null,
            2,
          ),
        },
      ],
    };
  } catch (error) {
    console.error("[GET_APP_ACTIONS] Error in execution:", error);
    return {
      $error: {
        message: `Error fetching app actions: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const getAppActionsDescription = `Fetches all available actions for a specific Zapier app implementation with caching.

This tool retrieves the complete list of actions (triggers, searches, creates) that an app supports,
filtered to only show actions relevant for field mapping operations.

Parameters:
- implementation_id: Full app identifier with version from get_app_name (e.g., "GmailCLIAPI@1.5.0")

Returns:
- Grouped actions by type (read, write, search)
- Action details (key, name, description, importance)
- Caching information (60-minute cache)
- Summary statistics

The results are cached for 60 minutes to improve performance. Cache is automatically invalidated
when a new version of the app is released (different implementation_id).

Use this after get_app_name to understand what the app can do before getting specific field requirements.`;

export const getAppActionsAtom = atom<
  JotaiTool<z.infer<typeof GetAppActionsParams>>
>(() => ({
  name: "get_app_actions",
  description: getAppActionsDescription,
  parameters: GetAppActionsParams,
  execute: atom(
    null, // read function (not used for write-only atom)
    async (_get, _set, { args }) => {
      return getAppActionsExecute(args);
    },
  ),
}));

getAppActionsAtom.debugLabel = "getAppActionsAtom";

/* eslint-enable sort-exports/sort-exports */
